import { log } from '@nx-superprep/utils'
import { Howler, Howl } from 'howler'
import { BehaviorSubject } from 'rxjs'
import { Choice } from '../card/choice.model'

export class Playlist {
  static readonly codecs = ["mp3", "mpeg", "opus", "ogg", "oga", "wav", "aac", "caf", "m4a", "m4b", "mp4", "weba", "webm", "dolby", "flac"].filter(c => Howler.codecs(c))
  static readonly codecsString = Playlist.codecs.join(",")

  private static readonly MAX_RETRIES = 3
  private static readonly RETRY_DELAY_MS = 1000

  isDone = new BehaviorSubject<boolean>(false)
  private _list: Howl[] = []
  private _playingIndex = -1
  private _isIOS: boolean
  private _retryCounts: Map<string, number> = new Map()

  constructor(options: Howl[] = []) {
    this._list = options
    this._isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  }

  get list(): Howl[] { return this._list; }
  get length(): number { return this._list.length; }
  get isPlaying(): boolean { return this._playingIndex !== -1; }
  get isLoaded(): boolean { return this._list.every(h => h.state() === 'loaded'); }
  get duration(): number { return this.isLoaded ? this._list.reduce((total, howl) => total + (howl.duration() || 0), 0) : 0 }

  load(audios: Choice[]): void {
    try {
      this.clear()
      this._retryCounts.clear()
      this._list = audios.map(c => this.createHowl(c))
    } catch (error) {
      log.error('Error loading audios:', error)
      this.clear()
    }
  }

  private createHowl(c: Choice): Howl {
    return new Howl({
      src: [c.audioUrl],
      volume: 1,
      html5: this._isIOS,
      preload: this._isIOS ? true : 'metadata',
      onunlock: () => log.debug('unlocked'),
      onload: () => {
        log.debug(`Loaded: ${c.audioUrl}`)
        this._retryCounts.delete(c.audioUrl)
      },
      onloaderror: (id, error) => this.handleLoadError(c, error),
      onplay: () => c.isActive = true,
      onend: () => this.next(),
      onstop: () => c.isActive = false,
      onpause: () => c.isActive = false,
    })
  }

  private handleLoadError(c: Choice, error: unknown): void {
    const errorMsg = typeof error === 'number'
      ? `Error code: ${error}`
      : `Unexpected error: ${error}`

    const currentRetryCount = this._retryCounts.get(c.audioUrl) || 0

    if (currentRetryCount < Playlist.MAX_RETRIES) {
      const nextRetryCount = currentRetryCount + 1
      this._retryCounts.set(c.audioUrl, nextRetryCount)

      log.warn(`Load error for ${c.audioUrl} (attempt ${nextRetryCount}/${Playlist.MAX_RETRIES}): ${errorMsg}. Retrying...`)

      setTimeout(() => {
        const index = this._list.findIndex(h => (h as any)._src === c.audioUrl)
        if (index !== -1) {
          this._list[index].unload()
          this._list[index] = this.createHowl(c)
        }
      }, Playlist.RETRY_DELAY_MS)
    } else {
      log.error(`Load error for ${c.audioUrl} after ${Playlist.MAX_RETRIES} attempts: ${errorMsg}. Codecs: ${Playlist.codecsString}`)
      this._retryCounts.delete(c.audioUrl)
    }
  }

  play(): void {
    try {
      if (this._playingIndex === -1 && this._list.length > 0) {
        this._playingIndex = 0
        this._list[this._playingIndex].play()
      }
    } catch (error) {
      log.error('Error playing audio:', error)
      this._playingIndex = -1
    }
  }

  next(): void {
    try {
      if (this._playingIndex !== -1) {
        this._list[this._playingIndex].stop()
        this._playingIndex++

        if (this._playingIndex < this._list.length) {
          this._list[this._playingIndex].play()
        } else {
          this.isDone.next(true)
          this._playingIndex = -1
        }
      }
    } catch (error) {
      log.error('Error moving to next audio:', error)
      this._playingIndex = -1
    }
  }

  stop(): void {
    try {
      if (this._playingIndex !== -1) {
        this._list[this._playingIndex].stop()
        this._playingIndex = -1
        this.isDone.next(true)
      }
    } catch (error) {
      log.error('Error stopping audio:', error)
      this._playingIndex = -1
    }
  }

  clear(): void {
    try {
      this._list.forEach(h => {
        try {
          h.stop()
          h.unload()
        } catch (error) {
          log.error('Error clearing Howl instance:', error)
        }
      })
      this._list = []
      this._playingIndex = -1
      this.isDone.next(false)
      this._retryCounts.clear()
    } catch (error) {
      log.error('Error clearing playlist:', error)
    }
  }
}
